/**
 * Pre-built theme presets that tenants can apply with one click.
 *
 * Each preset defines a harmonious color palette covering all 7
 * branding color slots. Applying a preset writes all values to the
 * tenant's branding record.
 */

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    tertiary: string;
    background: string;
    primaryBg: string;
    secondaryBg: string;
    tertiaryBg: string;
  };
  /** Optional font pairing. When present, applying the preset also sets fonts. */
  fonts?: {
    heading: string;
    body: string;
  };
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: "professional-blue",
    name: "Professional Blue",
    description: "Classic corporate blue with warm grays. Trusted and authoritative.",
    colors: {
      primary: "#2663C7",
      secondary: "#4A8FE7",
      tertiary: "#1B4F8A",
      background: "#1E3A5F",
      primaryBg: "#2663C7",
      secondaryBg: "#F0F4FA",
      tertiaryBg: "#E3ECF7",
    },
    fonts: { heading: "inter", body: "inter" },
  },
  {
    id: "ocean-teal",
    name: "Ocean Teal",
    description: "Calming teal and seafoam. Approachable and modern.",
    colors: {
      primary: "#0D9488",
      secondary: "#38BDF8",
      tertiary: "#115E59",
      background: "#134E4A",
      primaryBg: "#0D9488",
      secondaryBg: "#F0FDFA",
      tertiaryBg: "#CCFBF1",
    },
  },
  {
    id: "forest-green",
    name: "Forest Green",
    description: "Deep greens with earth tones. Natural and grounded.",
    colors: {
      primary: "#166534",
      secondary: "#65A30D",
      tertiary: "#14532D",
      background: "#1A3A2A",
      primaryBg: "#166534",
      secondaryBg: "#F0FDF4",
      tertiaryBg: "#DCFCE7",
    },
  },
  {
    id: "sunset-warmth",
    name: "Sunset Warmth",
    description: "Warm oranges and ambers. Energetic and inviting.",
    colors: {
      primary: "#C2410C",
      secondary: "#D97706",
      tertiary: "#9A3412",
      background: "#7C2D12",
      primaryBg: "#C2410C",
      secondaryBg: "#FFF7ED",
      tertiaryBg: "#FFEDD5",
    },
  },
  {
    id: "midnight-modern",
    name: "Midnight Modern",
    description: "Deep navy with electric accents. Bold and sophisticated.",
    colors: {
      primary: "#6366F1",
      secondary: "#A78BFA",
      tertiary: "#312E81",
      background: "#1E1B4B",
      primaryBg: "#4338CA",
      secondaryBg: "#EEF2FF",
      tertiaryBg: "#E0E7FF",
    },
    fonts: { heading: "raleway", body: "dm-sans" },
  },
  {
    id: "lavender-bloom",
    name: "Lavender Bloom",
    description: "Soft purples and lilacs. Elegant and creative.",
    colors: {
      primary: "#7C3AED",
      secondary: "#C084FC",
      tertiary: "#5B21B6",
      background: "#4C1D95",
      primaryBg: "#7C3AED",
      secondaryBg: "#FAF5FF",
      tertiaryBg: "#F3E8FF",
    },
  },
  {
    id: "coral-reef",
    name: "Coral Reef",
    description: "Vibrant coral with soft pinks. Warm and engaging.",
    colors: {
      primary: "#E11D48",
      secondary: "#FB7185",
      tertiary: "#9F1239",
      background: "#881337",
      primaryBg: "#E11D48",
      secondaryBg: "#FFF1F2",
      tertiaryBg: "#FFE4E6",
    },
  },
  {
    id: "emerald-gold",
    name: "Emerald & Gold",
    description: "Rich emerald paired with gold accents. Premium and distinguished.",
    colors: {
      primary: "#047857",
      secondary: "#B45309",
      tertiary: "#064E3B",
      background: "#064E3B",
      primaryBg: "#047857",
      secondaryBg: "#ECFDF5",
      tertiaryBg: "#D1FAE5",
    },
    fonts: { heading: "playfair", body: "source-serif" },
  },
  {
    id: "nordic-frost",
    name: "Nordic Frost",
    description: "Cool slate blues with icy accents. Clean and minimal.",
    colors: {
      primary: "#475569",
      secondary: "#64748B",
      tertiary: "#334155",
      background: "#1E293B",
      primaryBg: "#475569",
      secondaryBg: "#F8FAFC",
      tertiaryBg: "#F1F5F9",
    },
  },
  {
    id: "bordeaux",
    name: "Bordeaux",
    description: "Deep wine reds with warm neutrals. Classic and refined.",
    colors: {
      primary: "#9F1239",
      secondary: "#BE123C",
      tertiary: "#881337",
      background: "#4C0519",
      primaryBg: "#9F1239",
      secondaryBg: "#FFF1F2",
      tertiaryBg: "#FCE7F3",
    },
    fonts: { heading: "lora", body: "source-serif" },
  },
  {
    id: "citrus-fresh",
    name: "Citrus Fresh",
    description: "Bright lime and amber. Upbeat and youthful.",
    colors: {
      primary: "#65A30D",
      secondary: "#F59E0B",
      tertiary: "#3F6212",
      background: "#365314",
      primaryBg: "#65A30D",
      secondaryBg: "#F7FEE7",
      tertiaryBg: "#ECFCCB",
    },
  },
  {
    id: "charcoal-ember",
    name: "Charcoal & Ember",
    description: "Dark charcoal with warm red highlights. Strong and decisive.",
    colors: {
      primary: "#DC2626",
      secondary: "#F97316",
      tertiary: "#991B1B",
      background: "#1C1917",
      primaryBg: "#292524",
      secondaryBg: "#FAFAF9",
      tertiaryBg: "#F5F5F4",
    },
  },
];
